// ─────────────────────────────────────────────────────────────────────────────
// ChatWindowComponent
// ─────────────────────────────────────────────────────────────────────────────
// Wraps the deep-chat web component. Responsibilities:
//  - Sets up request.handler so deep-chat routes user messages through NgRx
//  - Feeds AI replies back to deep-chat via signals.onResponse
//  - Reloads message history when the active conversation changes
// ─────────────────────────────────────────────────────────────────────────────

import 'deep-chat';

import {
  AfterViewChecked,
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, take, takeUntil } from 'rxjs';

import { AppState } from 'src/app/core/app.state';
import { environment } from 'src/environments/environment';
import { IAiConversation, IAiMessage } from '../../models/ai-chat.models';
import { AiChatService } from '../../services/ai-chat.service';
import * as aiChatActions from '../../store/ai-chat.actions';

// ── Deep-chat web-component types ─────────────────────────────────────────────

interface ParsedReference {
  index: number;
  source: string;
  preview: string;
}

interface DeepChatElement extends HTMLElement {
  addMessage: (msg: { role: string; text?: string; html?: string }) => void;
  clearMessages: () => void;
  connect: unknown;
}

interface DeepChatRequestBody {
  messages: Array<{ role: string; text: string }>;
}

interface DeepChatSignals {
  onResponse: (response: { text?: string; html?: string }) => void;
  onClose: () => void;
}

type SendMessageDoneAction =
  | ReturnType<typeof aiChatActions.SendMessageSuccess>
  | ReturnType<typeof aiChatActions.SendMessageError>;

@Component({
  selector: 'app-chat-window',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './chat-window.component.html',
  styleUrls: ['./chat-window.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatWindowComponent implements AfterViewInit, AfterViewChecked, OnChanges, OnDestroy {

  // ── Decorated properties ───────────────────────────────────────────────────

  @Input() conversation: IAiConversation | null = null;
  @Input() loading = false;
  @ViewChild('deepChatEl') deepChatEl!: ElementRef<DeepChatElement>;

  // ── Public properties ──────────────────────────────────────────────────────

  readonly deepChatConnect = { stream: environment.aiChatUseStream, partialRender: true };

  readonly messageStyles = {
    default: {
      shared: { bubble: { maxWidth: '100%', fontSize: '14px', lineHeight: '1.5' } },
    },
  };

  textInput!: Record<string, unknown>;

  readonly submitButtonStyles = {
    submit: {
      container: {
        default: { transform: 'scale(1.21)', marginBottom: '-3px', marginRight: '0.4em' },
      },
    },
  };

  readonly auxiliaryStyle = `
    .ref-bar { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; padding-top: 8px; border-top: 1px solid #e0e0e0; }
    .ref-badge { display: flex; align-items: center; justify-content: center;
      width: 28px; height: 28px; border-radius: 50%; background:rgba(99, 75, 221, 0.2); color: #634bdd;
      font-size: 12px; font-weight: 600; line-height: 105%; cursor: pointer; }
  `;

  // ── Private properties ─────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();
  private previousConversationId: string | null = null;
  private handlerSet = false;

  // ── Constructor ────────────────────────────────────────────────────────────

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private ngZone: NgZone,
    private translate: TranslateService,
    private aiChatService: AiChatService
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    this.translate.get('ai_chat.input_placeholder').pipe(
      takeUntil(this.destroy$)
    ).subscribe((text: string) => {
      this.textInput = {
        placeholder: { text },
        styles: {
          container: {
            width: '100%',
            margin: '0',
            border: 'unset',
            borderTop: '1px solid #d5d5d5',
            borderRadius: '0px',
            boxShadow: 'unset',
          },
          text: {
            fontSize: '1.05em',
            paddingTop: '11px',
            paddingBottom: '13px',
            paddingLeft: '12px',
            paddingRight: '2.4em',
          },
        },
      };
    });
  }

  ngAfterViewChecked(): void {
    if (!this.handlerSet && this.deepChatEl?.nativeElement) {
      this.setupRequestHandler();
      setTimeout(() => {
        this.loadConversationHistory(this.conversation);
        this.previousConversationId = this.conversation?.id ?? null;
      }, 100);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['conversation']) {
      const next: IAiConversation | null = changes['conversation'].currentValue;
      if (next?.id !== this.previousConversationId) {
        this.loadConversationHistory(next);
        this.previousConversationId = next?.id ?? null;
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.querySelectorAll('.ai-ref-tooltip').forEach(t => t.remove());
  }

  // ── Deep-chat integration ──────────────────────────────────────────────────

  /**
   * Registers a request.handler on the deep-chat element so all user messages
   * are routed through NgRx instead of an HTTP endpoint.
   */
  private setupRequestHandler(): void {
    const el = this.deepChatEl?.nativeElement;
    if (!el) return;

    this.handlerSet = true;
    el.focus();

    (el as any).htmlClassUtilities = {
      'ref-badge': {
        events: {
          click: (event: MouseEvent) => {
            event.stopPropagation();
            const existing = document.querySelector('.ai-ref-tooltip');
            const badge = event.currentTarget as HTMLElement;

            if (existing && existing.getAttribute('data-for') === badge.getAttribute('data-tooltip')) {
              existing.remove();
              return;
            }
            document.querySelectorAll('.ai-ref-tooltip').forEach(t => t.remove());

            const content = badge.getAttribute('data-tooltip');
            if (!content) return;

            const tip = document.createElement('div');
            tip.className = 'ai-ref-tooltip';
            tip.setAttribute('data-for', content);
            tip.innerHTML = content;
            tip.addEventListener('click', (e: Event) => e.stopPropagation());
            tip.addEventListener('mousedown', (e: Event) => e.stopPropagation());
            document.body.appendChild(tip);

            const rect = badge.getBoundingClientRect();
            tip.style.left = `${rect.left + rect.width / 2}px`;
            tip.style.top = `${rect.top - 6}px`;
          },
        },
      },
    };

    document.addEventListener('click', (e: MouseEvent) => {
      if ((e.target as HTMLElement)?.closest?.('.ai-ref-tooltip')) return;
      document.querySelectorAll('.ai-ref-tooltip').forEach(t => t.remove());
    });

    el.connect = {
      ...this.deepChatConnect,
      handler: (body: DeepChatRequestBody, signals: DeepChatSignals) => {
        const lastMsg = body.messages[body.messages.length - 1];

        this.ngZone.run(() => {
          this.store.dispatch(aiChatActions.SendMessage({ text: lastMsg.text }));
        });

        const done$ = this.actions$.pipe(
          ofType(aiChatActions.SendMessageSuccess, aiChatActions.SendMessageError),
          take(1),
          takeUntil(this.destroy$)
        );

        if (environment.aiChatUseStream) {
          this.subscribeToStreamChunks(signals, done$);
        }

        this.subscribeToCompletion(signals, done$);
      },
    };
  }

  /** Forwards each stream chunk from the service to deep-chat. */
  private subscribeToStreamChunks(signals: DeepChatSignals, done$: Observable<SendMessageDoneAction>): void {
    this.aiChatService.streamChunk$.pipe(
      takeUntil(done$),
      takeUntil(this.destroy$)
    ).subscribe((text) => signals.onResponse({ text }));
  }

  /** Handles final success/error for both stream and non-stream modes. */
  private subscribeToCompletion(signals: DeepChatSignals, done$: Observable<SendMessageDoneAction>): void {
    done$.subscribe((action) => {
      if (action.type === aiChatActions.SendMessageSuccess.type) {
        if (!environment.aiChatUseStream) {
          const lastAiMessage = [...action.conversation.messages].reverse().find((m: IAiMessage) => m.role === 'ai');
          const rawText = lastAiMessage?.text ?? '';
          const answer = this.extractAnswer(rawText);
          const references = lastAiMessage?.references?.map(r => {
            return {
              index: r.id,
              source: r.source,
              preview: r.excerpt,
            };
          }) ?? [];
          signals.onResponse(references.length
            ? { html: this.buildAiHtml(answer, references) }
            : { text: answer });
        }
      } else {
        signals.onResponse({ text: this.translate.instant('ai_chat.error_generic') });
      }
      signals.onClose();
    });
  }

  // ── Response parsing ───────────────────────────────────────────────────────

  /** Extracts the [ANSWER] block from AI responses; returns the full text if no block is found. */
  private extractAnswer(text: string): string {
    const match = text.match(/\[ANSWER\]\s*([\s\S]*?)\s*\[\/ANSWER\]/);
    return match ? match[1].trim() : text;
  }

  /** Parses the [REFERENCES] block into structured objects. Preview text may span multiple lines. */
  private extractReferences(text: string): ParsedReference[] {
    const block = text.match(/\[REFERENCES\]\s*([\s\S]*?)\s*\[\/REFERENCES\]/);
    if (!block) return [];
    const refs: ParsedReference[] = [];
    const refRegex = /\[(\d+)\]\s*([^|]+)\|[^|]*\|\s*"([\s\S]*?)"/g;
    let m: RegExpExecArray | null;
    while ((m = refRegex.exec(block[1])) !== null) {
      refs.push({ index: +m[1], source: m[2].trim(), preview: m[3].trim() });
    }
    return refs;
  }

  /** Builds an HTML string with the answer text and reference badges with tooltips. */
  private buildAiHtml(answer: string, refs: ParsedReference[]): string {
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const badgesHtml = refs.map(r => {
      // const preview = r.preview.length > 120 ? r.preview.substring(0, 120) + '...' : r.preview;
      const preview = '';
      const tip = `
        <b>
         <img src='assets/icones/file-primary.svg' alt='Reference Badge' />
          ${esc(r.source)}
        </b>
        <blockquote>${esc(preview)}</blockquote>
      `;
      return `<span class="ref-badge" data-tooltip="${tip}">[${r.index}]</span>`;
    }).join(' ');
    return `<div class="ai-answer">${answer}</div><div class="ref-bar">${badgesHtml}</div>`;
  }

  private loadConversationHistory(conversation: IAiConversation | null): void {
    const el = this.deepChatEl?.nativeElement;
    if (!el) return;

    el.clearMessages();
    if (!conversation?.messages.length) return;

    conversation.messages.forEach((msg: IAiMessage) => {
      if (msg.role === 'ai') {
        const answer = this.extractAnswer(msg.text);
        const refs = this.extractReferences(msg.text);
        el.addMessage(refs.length
          ? { role: msg.role, html: this.buildAiHtml(answer, refs) }
          : { role: msg.role, text: answer });
      } else {
        el.addMessage({ role: msg.role, text: msg.text });
      }
    });
  }
}
